/**
 * Gráfico de barras agrupadas (N series) en SVG puro. Sin dependencias.
 * Cada grupo (label) dibuja una barra por serie, con leyenda debajo.
 * Pensado para "Vendido/Facturado/Cobrado por proyecto" y series mensuales.
 */
import { useTranslation } from 'react-i18next'
import { ChartTooltip, useChartTooltip } from './ChartTooltip'

export interface BarSeries {
  name: string
  color: string
}

interface DataGroup {
  label: string
  values: number[]
}

interface Props {
  data: DataGroup[]
  series: BarSeries[]
  formatValue?: (n: number) => string
  /** Formateador COMPACTO para el eje Y (por defecto = formatValue). */
  formatAxis?: (n: number) => string
  height?: number
  /** Rota las etiquetas del eje X (útil cuando son nombres largos). */
  rotateLabels?: boolean
}

export default function MultiBarChart({
  data,
  series,
  formatValue = (n) => String(n),
  formatAxis,
  height = 240,
  rotateLabels = false,
}: Props) {
  const axisFmt = formatAxis ?? formatValue
  const { t } = useTranslation('common')
  const { containerRef, tip, show, hide } = useChartTooltip()
  if (data.length === 0) {
    return <div className="text-xs t-faint text-center py-8">{t('barChart.noData')}</div>
  }

  const max = Math.max(1, ...data.flatMap((d) => d.values))
  const W = 640
  const H = height
  const padding = { top: 16, right: 16, bottom: rotateLabels ? 64 : 32, left: 66 }
  const innerW = W - padding.left - padding.right
  const innerH = H - padding.top - padding.bottom
  const groupW = innerW / data.length
  const nSeries = series.length
  const barW = (groupW * 0.7) / nSeries
  const groupInner = barW * nSeries

  const grid = [0.25, 0.5, 0.75, 1].map((p) => {
    const y = padding.top + innerH * (1 - p)
    return { y, label: axisFmt(max * p) }
  })

  return (
    <div ref={containerRef} className="relative flex flex-col gap-3" onMouseLeave={hide}>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" preserveAspectRatio="xMidYMid meet">
        {grid.map((g, i) => (
          <g key={i}>
            <line
              x1={padding.left}
              x2={W - padding.right}
              y1={g.y}
              y2={g.y}
              stroke="currentColor"
              strokeOpacity={0.08}
              strokeDasharray="2 3"
            />
            <text
              x={padding.left - 6}
              y={g.y + 4}
              textAnchor="end"
              className="text-[9px] fill-current opacity-50"
            >
              {g.label}
            </text>
          </g>
        ))}

        {data.map((d, i) => {
          const groupX = padding.left + i * groupW + (groupW - groupInner) / 2
          const labelX = padding.left + i * groupW + groupW / 2
          const tipData = {
            title: d.label,
            rows: d.values.map((v, s) => ({
              label: series[s]?.name,
              value: formatValue(v),
              color: series[s]?.color,
            })),
          }
          return (
            <g key={i}>
              {d.values.map((v, s) => {
                const h = (v / max) * innerH
                const x = groupX + s * barW
                return (
                  <rect
                    key={s}
                    x={x}
                    y={padding.top + innerH - h}
                    width={Math.max(1, barW - 1)}
                    height={h}
                    fill={series[s]?.color ?? '#888'}
                    rx={1.5}
                    onMouseMove={(e) => show(e, tipData)}
                  >
                    <title>
                      {d.label} · {series[s]?.name}: {formatValue(v)}
                    </title>
                  </rect>
                )
              })}
              <text
                x={labelX}
                y={H - padding.bottom + 12}
                textAnchor={rotateLabels ? 'end' : 'middle'}
                transform={rotateLabels ? `rotate(-35 ${labelX} ${H - padding.bottom + 12})` : undefined}
                className="text-[9px] fill-current opacity-60"
              >
                {d.label.length > 18 ? `${d.label.slice(0, 17)}…` : d.label}
              </text>
            </g>
          )
        })}
      </svg>

      <div className="flex items-center justify-center gap-4 text-xs t-secondary flex-wrap">
        {series.map((s, i) => (
          <span key={i} className="flex items-center gap-1.5">
            <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: s.color }} />
            {s.name}
          </span>
        ))}
      </div>
      <ChartTooltip tip={tip} />
    </div>
  )
}
